import React, { useMemo } from 'react';

import { Table } from 'antd';
import { uniq } from 'lodash';

import { GetTimestampFromDate, isSameDate, showDateNoTime } from '@libs/NwMoment';

import { useLayoutContext } from '@contexts/LayoutContext';
import { useUserPermission } from '@contexts/UserContext';

import { getContactName } from '@hooks/API/Contact/UseContactsList';
import useConfigurations from '@hooks/Configurations/UseConfigurations';

import VerticalDate from '@components/Dates/VerticalDate';
import Money from '@components/Gui/Money';
import NwIcon from '@components/Gui/NwIcon';
import StyleVariables from '@components/Gui/StyleVariables';
import { getDineroAmount } from '@components/StatementRowsCommon/helpers';

import {
    ProjectSummaryActionsCell,
    adaptProjectSummaryRows,
    ProjectSummaryAmountCell,
    ProjectSummaryMetaCell,
    ProjectSummaryStatusCell,
    ProjectSummaryTitleCell,
} from './ProjectSummaryRowsCommon';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const buildRowsForDate = (transactions, modelId, workDate) => {
    return adaptProjectSummaryRows(transactions, { modelId, workDate });
};

const groupProjectSummaryTotalsByCurrency = (rows, defaultCurrency) => {
    if (!Array.isArray(rows) || rows.length === 0) {
        return [];
    }

    const totalsByCurrency = {};

    for (const row of rows) {
        const resolvedCurrency = row.Currency || defaultCurrency;

        if (!totalsByCurrency[resolvedCurrency]) {
            totalsByCurrency[resolvedCurrency] = getDineroAmount(0, resolvedCurrency, defaultCurrency);
        }

        totalsByCurrency[resolvedCurrency] = totalsByCurrency[resolvedCurrency].add(
            getDineroAmount(row.TotalFee, resolvedCurrency, defaultCurrency)
        );
    }

    return Object.entries(totalsByCurrency)
        .sort(([currencyA], [currencyB]) => {
            if (currencyA === defaultCurrency) {
                return -1;
            }
            if (currencyB === defaultCurrency) {
                return 1;
            }
            return currencyA.localeCompare(currencyB);
        })
        .map(([Currency, Dinero]) => ({
            Currency,
            Dinero,
        }));
};

const ProjectGroupedTransactionsListItem = ({
    eventDrawer,
    isExpanded,
    model,
    onEditTransaction,
    project,
    readOnly,
    showTotals = false,
}) => {
    const ViewAgencyFee = useUserPermission('ViewAgencyFee');
    const ViewAgencyCommission = useUserPermission('ViewAgencyCommission');

    const { isMobile } = useLayoutContext();
    const configurations = useConfigurations();

    const isItemExpanded = () => {
        return Boolean(isExpanded);
    };

    const transactionColumns = [
        {
            title: 'Status',
            key: 'Status',
            align: 'center',
            className: 'cell-border-right',
            render: (record) => <ProjectSummaryStatusCell row={record} />,
        },
        {
            title: 'Title',
            key: 'Title',
            width: eventDrawer ? '100%' : '60%',
            className: 'cell-border-right',
            render: (record) => (
                <div className="transaction-meta-wrapper">
                    <ProjectSummaryTitleCell row={record} />
                    {eventDrawer && (
                        <ProjectSummaryMetaCell row={record} project={project} isExpanded={isItemExpanded()} />
                    )}
                </div>
            ),
        },
        {
            title: 'Meta',
            key: 'Meta',
            width: '40%',
            className: 'cell-border-right',
            render: (record) => <ProjectSummaryMetaCell row={record} project={project} isExpanded={isItemExpanded()} />,
        },
        {
            title: 'Amount',
            key: 'Amount',
            // width: '100px',
            align: 'right',
            className: 'cell-border-right',
            render: (record) => (
                <ProjectSummaryAmountCell
                    row={record}
                    isExpanded={eventDrawer ? true : isItemExpanded()}
                    showAgencyFee={ViewAgencyFee}
                    showAgencyCommission={ViewAgencyCommission}
                    showAdvance
                    highlightCurrency
                />
            ),
        },
        {
            title: 'Actions',
            key: 'Actions',
            align: 'center',
            render: (record) => (
                <ProjectSummaryActionsCell
                    row={record}
                    onEditTransaction={readOnly ? undefined : onEditTransaction}
                    ghost
                />
            ),
        },
    ];

    if (eventDrawer) {
        transactionColumns.splice(2, 1);
    }

    // const transactionColumnsEventDrawer = [
    //     {
    //         title: 'Title',
    //         key: 'Title',
    //         width: '100%',
    //         render: (record) => (
    //             <div>
    //                 <ProjectSummaryTitleCell row={record} />
    //                 <ProjectSummaryMetaCell row={record} project={project} />
    //             </div>
    //         ),
    //     },
    //     {
    //         title: 'Amount',
    //         key: 'Amount',
    //         align: 'right',
    //         render: (record) => (
    //             <ProjectSummaryAmountCell
    //                 row={record}
    //                 isExpanded={isItemExpanded()}
    //                 showAgencyFee={ViewAgencyFee}
    //                 showAgencyCommission={ViewAgencyCommission}
    //             />
    //         ),
    //     },
    //     {
    //         title: 'Actions',
    //         key: 'Actions',
    //         width: 72,
    //         align: 'center',
    //         render: (record) => (
    //             <ProjectSummaryActionsCell
    //                 row={record}
    //                 onEditTransaction={readOnly ? undefined : onEditTransaction}
    //                 ghost
    //             />
    //         ),
    //     },
    // ];

    const mobileTransactionColumns = [
        {
            title: 'Title',
            key: 'Title',
            width: '80%',
            render: (record) => {
                const expanded = isItemExpanded();

                return (
                    <>
                        <ProjectSummaryTitleCell row={record} />
                        <ProjectSummaryAmountCell
                            row={record}
                            isExpanded={expanded}
                            showAgencyFee={ViewAgencyFee}
                            showAgencyCommission={ViewAgencyCommission}
                            className="transaction-right transaction-right-no-border"
                        />
                    </>
                );
            },
        },
        {
            title: 'Edit',
            key: 'Edit',
            width: '20%',
            render: (record) => (
                <ProjectSummaryActionsCell
                    row={record}
                    onEditTransaction={readOnly ? undefined : onEditTransaction}
                    ghost
                />
            ),
        },
    ];

    const isDateCancelled = (date) => {
        return date.Transactions.every((transaction) => transaction.WorkDateCancelled);
    };

    const isTransactionConfirmed = (record) => {
        const statements = record.ClientStatements || record.Transaction?.ClientStatements || [];

        if (statements.length > 0) {
            return statements[0].Status !== 'Option';
        }

        return true;
    };

    const confirmedDatesWithoutTransactions = useMemo(() => {
        const eventDatesModels =
            project?.Events?.flatMap((event) => event.EventDates.flatMap((date) => date.EventDatesModels)) || [];
        const confirmedEventDatesModels = eventDatesModels.filter(
            (item) => item.OptionNumber === 10 && !item.Cancel && item.ModelID === model.ModelID
        );
        const confirmedDates = uniq(confirmedEventDatesModels.map((item) => item.FromDate)).sort();

        return confirmedDates.filter((date) => !model.Dates.some((item) => isSameDate(item.Date, date)));
    }, [model, project]);

    return (
        <div className="subject-container">
            {model.ModelID ? <h2>{getContactName(model.ModelID)}</h2> : <h2>General</h2>}
            {model.Dates.map((date) => {
                const rows = buildRowsForDate(date.Transactions || [], model.ModelID, date.Date);
                const displayedColumns = isMobile ? mobileTransactionColumns : transactionColumns;
                const titleColumnIndex = displayedColumns.findIndex((column) => column.key === 'Title');
                const amountColumnIndex = displayedColumns.findIndex((column) => column.key === 'Amount');
                const totals = showTotals
                    ? groupProjectSummaryTotalsByCurrency(rows, configurations.defaultCurrency)
                    : [];
                const summaryCellStyle = {
                    padding: 0,
                    backgroundColor: 'white',
                    borderTop: `2px solid ${StyleVariables.PrimaryColor}`,
                };

                return (
                    <div
                        className="date-container"
                        key={`model-${model.ModelID ? model.ModelID : '0'}-${date.Date ? GetTimestampFromDate(date.Date) : '0'}`}
                    >
                        <VerticalDate size="small" date={date.Date} cancelled={isDateCancelled(date)} />
                        <div className="transactions-container">
                            <Table
                                showHeader={false}
                                pagination={false}
                                columns={displayedColumns}
                                size="small"
                                rowClassName={(record) => {
                                    let cn = '';
                                    if (record.WorkDateCancelled) {
                                        cn += 'cancelled';
                                    }
                                    if (!isTransactionConfirmed(record)) {
                                        cn += ' not-confirmed';
                                    }
                                    return cn;
                                }}
                                dataSource={rows}
                                summary={
                                    showTotals
                                        ? () => {
                                              if (totals.length === 0) {
                                                  return null;
                                              }

                                              if (isMobile || amountColumnIndex === -1) {
                                                  return (
                                                      <Table.Summary.Row>
                                                          <Table.Summary.Cell
                                                              index={0}
                                                              style={{
                                                                  ...summaryCellStyle,
                                                                  padding: '0.5rem 0.75rem',
                                                              }}
                                                          >
                                                              <div className="summary-icon-wrapper">
                                                                  <NwIcon icon={byPrefixAndName.fass['sigma']} />
                                                              </div>
                                                          </Table.Summary.Cell>
                                                          <Table.Summary.Cell
                                                              index={1}
                                                              style={{
                                                                  ...summaryCellStyle,
                                                                  padding: '0.5rem 0.75rem',
                                                              }}
                                                          >
                                                              <small
                                                                  style={{
                                                                      display: 'block',
                                                                      textTransform: 'uppercase',
                                                                      textAlign: 'right',
                                                                  }}
                                                              >
                                                                  totals
                                                              </small>
                                                              {totals.map((item) => (
                                                                  <Money
                                                                      key={item.Currency}
                                                                      dineroObj={item.Dinero}
                                                                      boldIfDefaultCurrency
                                                                      style={{ display: 'block', textAlign: 'right' }}
                                                                  />
                                                              ))}
                                                          </Table.Summary.Cell>
                                                      </Table.Summary.Row>
                                                  );
                                              }

                                              return (
                                                  <Table.Summary.Row>
                                                      <Table.Summary.Cell index={0} style={summaryCellStyle}>
                                                          <div className="summary-icon-wrapper">
                                                              <NwIcon icon={byPrefixAndName.fass['sigma']} />
                                                          </div>
                                                      </Table.Summary.Cell>
                                                      {titleColumnIndex > 1 && (
                                                          <Table.Summary.Cell
                                                              index={1}
                                                              colSpan={titleColumnIndex - 1}
                                                              style={summaryCellStyle}
                                                          />
                                                      )}
                                                      <Table.Summary.Cell
                                                          index={titleColumnIndex}
                                                          style={summaryCellStyle}
                                                      >
                                                          <div
                                                              style={{
                                                                  textTransform: 'uppercase',
                                                                  padding: '0.5rem 0.75rem',
                                                              }}
                                                          >
                                                              <small>totals</small>
                                                          </div>
                                                      </Table.Summary.Cell>
                                                      {amountColumnIndex - titleColumnIndex > 1 && (
                                                          <Table.Summary.Cell
                                                              index={titleColumnIndex + 1}
                                                              colSpan={amountColumnIndex - titleColumnIndex - 1}
                                                              style={summaryCellStyle}
                                                          />
                                                      )}
                                                      <Table.Summary.Cell
                                                          index={amountColumnIndex}
                                                          style={summaryCellStyle}
                                                      >
                                                          <div
                                                              style={{ padding: '0.5rem 0.75rem', lineHeight: '1.3em' }}
                                                          >
                                                              {totals.map((item) => (
                                                                  <Money
                                                                      key={item.Currency}
                                                                      dineroObj={item.Dinero}
                                                                      boldIfDefaultCurrency
                                                                      style={{ display: 'block', textAlign: 'right' }}
                                                                  />
                                                              ))}
                                                          </div>
                                                      </Table.Summary.Cell>
                                                      <Table.Summary.Cell
                                                          index={amountColumnIndex + 1}
                                                          style={summaryCellStyle}
                                                      />
                                                  </Table.Summary.Row>
                                              );
                                          }
                                        : undefined
                                }
                            />
                        </div>
                    </div>
                );
            })}
            {confirmedDatesWithoutTransactions.length > 0 && (
                <div className="date-container">
                    <div className="dates-without-transactions">
                        <div className="icon-wrapper">
                            <NwIcon icon={byPrefixAndName.fass['triangle-exclamation']} danger />
                        </div>
                        <p>confirmed dates without transactions:</p>
                        <div className="dates-wrapper">
                            {confirmedDatesWithoutTransactions.map((date) => (
                                <span key={showDateNoTime(date)} className="date-item">
                                    {showDateNoTime(date)}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProjectGroupedTransactionsListItem;
